import PublicApiService from "../../../../services/PublicApiService";
import { BadgeOrder, GetOrdersResponse } from "../../../../redux/types";
import { useDispatch, useStore } from "react-redux";
import { useRef } from "react";
import dayjs from "../../../../common/dayjs";
import { RootState } from "../../../../redux/store";
import { setBadgeOrders } from "../../../../redux/eventEdition/slice/eventEditionSlice";
import { badgeSyncRequestStarted, badgeSyncRequestCompleted, badgeSyncRequestFailed } from "redux/metadata/slice/metadataSlice";

export const useFetchBadgeOrders = () => {
  const dispatch = useDispatch();
  const badgeFetchInterval = useRef<NodeJS.Timer | null>(null);
  const { getState } = useStore();
  const orders = useRef<BadgeOrder[]>([]);

  const mergeOrdersByOrderId = (items: BadgeOrder[]) => {
    const map = new Map<string, BadgeOrder>();
    items.forEach(order => {
      if (order?.badgeId && order?.orderId) {
        map.set(`${order.badgeId}:${order.orderId}`, order);
      }
    });
    return Array.from(map.values());
  };

  const handlePagination = async (apiResponse: GetOrdersResponse, eventGbsCode: string) => {
    if (!apiResponse.token) {
      return;
    }

    const response = await PublicApiService.getBadgeOrders(eventGbsCode, apiResponse.token);
    orders.current = [...orders.current, ...response.results];
    await handlePagination(response, eventGbsCode);
  };

  const fetchBadgeOrders = async (eventGbsCode: string) => {
    dispatch(badgeSyncRequestStarted());
    try {
      const apiResponse = await PublicApiService.getBadgeOrders(eventGbsCode);
      orders.current = [...apiResponse.results];
      await handlePagination(apiResponse, eventGbsCode);
      orders.current = mergeOrdersByOrderId(orders.current);
      dispatch(setBadgeOrders(orders.current));
      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      dispatch(badgeSyncRequestFailed());
    }
  };

  const fetchBadgeOrdersForShows = async (eventGbsCodes: string[]) => {
    dispatch(badgeSyncRequestStarted());
    try {
      orders.current = [];
      for (const gbsCode of eventGbsCodes) {
        const apiResponse = await PublicApiService.getBadgeOrders(gbsCode);
        orders.current = [...orders.current, ...apiResponse.results];
        await handlePagination(apiResponse, gbsCode);
      }
      orders.current = mergeOrdersByOrderId(orders.current);
      dispatch(setBadgeOrders(orders.current));
      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      dispatch(badgeSyncRequestFailed());
    }
  };

  const fetchAndReplaceOrders = async (eventGbsCode: string) => {
    try {
      const { eventEdition, metadata } = getState() as RootState;

      const apiResponse = await PublicApiService.getBadgeOrders(
        eventGbsCode,
        "",
        dayjs(metadata.lastBadgeUpdateTime).utc().format("YYYY-MM-DDTHH:mm:ss")
      );

      if (apiResponse.results?.length === 0) {
        return;
      }
      let ordersFromStore: BadgeOrder[] = [...eventEdition.orders];

      apiResponse.results.forEach(res => {
        const badgeOrders = ordersFromStore.filter(o => o.badgeId === res.badgeId && !o.isGeneratedLocal);
        ordersFromStore = [...ordersFromStore.filter(o => o.badgeId !== res.badgeId), ...badgeOrders];
      });

      dispatch(setBadgeOrders(ordersFromStore));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAndReplaceOrdersForShows = async (eventGbsCodes: string[]) => {
    try {
      const { eventEdition, metadata } = getState() as RootState;
      let ordersFromStore: BadgeOrder[] = [...eventEdition.orders];

      for (const gbsCode of eventGbsCodes) {
        const apiResponse = await PublicApiService.getBadgeOrders(
          gbsCode,
          "",
          dayjs(metadata.lastBadgeUpdateTime).utc().format("YYYY-MM-DDTHH:mm:ss")
        );

        if (apiResponse.results?.length === 0) {
          continue;
        }

        apiResponse.results.forEach(res => {
          const badgeOrders = ordersFromStore.filter(o => o.badgeId === res.badgeId && !o.isGeneratedLocal);
          ordersFromStore = [...ordersFromStore.filter(o => o.badgeId !== res.badgeId), ...badgeOrders];
        });

        ordersFromStore = mergeOrdersByOrderId([...ordersFromStore, ...apiResponse.results]);
      }

      dispatch(setBadgeOrders(ordersFromStore));
    } catch (e) {
      console.error(e);
    }
  };

  const startOrderFetchInterval = async (eventGbsCode: string) => {
    if (badgeFetchInterval.current) {
      clearInterval(badgeFetchInterval.current);
    }
    badgeFetchInterval.current = setInterval(() => {
      fetchAndReplaceOrders(eventGbsCode);
    }, 30000);
  };

  const startOrderFetchIntervalForShows = async (eventGbsCodes: string[]) => {
    if (badgeFetchInterval.current) {
      clearInterval(badgeFetchInterval.current);
    }
    badgeFetchInterval.current = setInterval(() => {
      fetchAndReplaceOrdersForShows(eventGbsCodes);
    }, 30000);
  };

  const clearBadgeFetchInterval = async () => {
    clearInterval(badgeFetchInterval.current!);
  };

  const recallForBadgeOrders = async (badgeId: string) => {
    try {
      return await PublicApiService.getBadgeOrdersByBadgeId(badgeId);
    } catch (error) {
      console.error("Error fetching badge orders:", error);
    }
  };

  return {
    fetchBadgeOrders,
    fetchBadgeOrdersForShows,
    startOrderFetchInterval,
    startOrderFetchIntervalForShows,
    clearBadgeFetchInterval,
    fetchAndReplaceOrders,
    fetchAndReplaceOrdersForShows,
    recallForBadgeOrders
  };
};
